import { isInvisibleUser } from 'helpers/userHelper';
import { useComplexState } from 'hook/helper.hook';
import { useDispatch } from 'react-redux';
import { useLogger } from 'hook/error.hook';
import {
  sortList,
  DEFAULT_LOAN_AMOUNT,
  getRefinanceAccount,
} from 'helpers/autoLoan.helper';
import { useEffect } from 'react';
import { getOpenAutoLoanAccounts } from 'helpers/credit_info';
import { loadAutoLoanOffers } from 'actions/autoLoan.actions';
import { getGetParam } from 'helpers/domHelper';
import useSelectAutoLoanDetails from 'hook/selectAutoLoanDetails.hook';

export const useAutoMarketplace = ({
  creditInfo,
  userInfo,
  isRefinanceOffersRequired = true,
  isPurchaseOffersRequired = false,
  autoLoanTab,
}) => {
  useSelectAutoLoanDetails({ autoLoanType: autoLoanTab?.selectedTab });

  const logger = useLogger({
    name: 'AutoMarketplace.hook',
  });

  const dispatch = useDispatch();

  const autoLoanAccounts = getOpenAutoLoanAccounts(creditInfo);
  let showAutoLoanList = autoLoanAccounts && autoLoanAccounts.length > 1;
  const refinanceAccount = autoLoanAccounts
    ? getRefinanceAccount(autoLoanAccounts)
    : null;

  const autoRefinanceForProminance = getGetParam('auto_refinance_key');

  const [state, setState] = useComplexState({
    autoLoanOffersLoading: false,
    autoLoanOffersError: false,
    autoLoanOffers: null,

    // refinance state

    currentLoanDetails: null,
    refinanceOffersLoading: false,
    refinanceOffersError: false,
    refinanceOffers: null,
    topRefinanceOffer: null,
    seeAutoRefinanceLoans: false,
    genericAutoLoanOffers: null,
    genericRefinanceLoanOffers: null,
  });

  useEffect(() => {
    if (userInfo && creditInfo) {
      if (isPurchaseOffersRequired) {
        getAutoLoans({
          currentSortType: sortList[0],
          loanAmount: DEFAULT_LOAN_AMOUNT,
        });
      }
      if (isRefinanceOffersRequired && refinanceAccount) {
        getAutoRefinance({ currentSortType: sortList[0], refinanceAccount });
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo, creditInfo, refinanceAccount]);

  const getAutoLoans = props => {
    setState({
      autoLoanOffersLoading: true,
    });

    getAutoLoanOffers(props);
  };

  const getAutoLoanOffers = ({ currentSortType = sortList[0], loanAmount }) => {
    dispatch(
      loadAutoLoanOffers({
        currentSortType,
        autoLoanType: 'Purchase',
        creditInfo,
        userInfo,
        loanAmount,
      }),
    )
      .then(res => {
        setState({
          autoLoanOffers: res.autoLoanOffers,
          autoLoanOffersLoading: false,
          genericAutoLoanOffers: res.genericRefinanceLoanOffers,
        });
      })
      .catch(err => {
        logger.reportAPIError(err);
        logger.createFatalApplicationError(err);
        setState({
          autoLoanOffersError: true,
          autoLoanOffersLoading: false,
        });
      });
  };

  const getAutoRefinance = props => {
    setState({
      refinanceOffersLoading: true,
    });
    getAutoRefinanceOffers(props);
  };

  const getAutoRefinanceOffers = ({
    currentSortType = sortList[0],
    refinanceAccount,
  }) => {
    dispatch(
      loadAutoLoanOffers({
        currentSortType,
        autoLoanType: 'Refinance',
        creditInfo,
        userInfo,
        refinanceAccount,
      }),
    )
      .then(res => {
        let sortedOffers = res.autoLoanOffers;
        if (autoRefinanceForProminance) {
          sortedOffers = res.autoLoanOffers.sort((a, b) => {
            if (a.productKey === autoRefinanceForProminance) return -1;
            if (b.productKey === autoRefinanceForProminance) return 1;
            return 0;
          });
        }

        setState({
          refinanceOffers: sortedOffers,
          topRefinanceOffer: res.topRefinanceOffer,
          refinanceOffersLoading: false,
          genericRefinanceLoanOffers: res.genericRefinanceLoanOffers,
          currentLoanDetails: res.currentLoanDetails,
          seeAutoRefinanceLoans: true,
        });
      })
      .catch(err => {
        logger.reportAPIError(err);
        logger.createFatalApplicationError(err);
        setState({
          refinanceOffersError: true,
          refinanceOffersLoading: false,
        });
      });
  };

  let readyForTab = false;

  // this logic should be deprecated at least for Purchase tab , as experiment is 100% , carvana exp
  // this logic should be deprecated Refinance tab , if experiment is set 100% var1 , auto redesign exp

  if (autoLoanTab?.selectedTab === 'Refinance') {
    readyForTab =
      showAutoLoanList ||
      !isRefinanceOffersRequired ||
      (isRefinanceOffersRequired && state.refinanceOffers);
  } else if (autoLoanTab?.selectedTab === 'Purchase') {
    readyForTab =
      !isPurchaseOffersRequired ||
      (isPurchaseOffersRequired && state.autoLoanOffers);
  }

  const readyForContainer =
    userInfo &&
    (creditInfo || isInvisibleUser(userInfo)) &&
    (readyForTab || isInvisibleUser(userInfo));

  const setRefinanceOffers = offers => {
    setState({
      refinanceOffers: offers,
    });
  };

  return {
    getAutoLoans,
    getAutoRefinance,
    setRefinanceOffers,
    ...state,
    readyForContainer,
    showAutoLoanList,
    autoLoanAccounts,
  };
};
